import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Tooltip } from "react-tooltip";
import type { PlacesType } from "react-tooltip";

import { cn } from "@/utils/styles";

type SimpleTooltipType = {
  children: React.ReactNode;
  content: React.ReactNode;
  id: string;
  className?: string;
  place?: PlacesType;
  tooltipClassName?: string;
  openOnClick?: boolean;
  isOpen?: boolean;
  delayHide?: number;
  /**
   * Render the tooltip into `document.body` with a fixed position strategy so it
   * escapes ancestors that clip overflow (e.g. `overflow-hidden`). Opt-in to avoid
   * changing the default render behavior for existing usages.
   */
  usePortal?: boolean;
};

export const SimpleTooltip = ({
  children,
  content,
  id,
  className,
  place,
  openOnClick = false,
  isOpen,
  delayHide,
  tooltipClassName = "",
  usePortal = false
}: SimpleTooltipType) => {
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (usePortal) {
      setPortalRoot(document.body);
    }
  }, [usePortal]);

  const tooltip = (
    <Tooltip
      isOpen={isOpen}
      openOnClick={openOnClick}
      delayHide={delayHide}
      opacity={1}
      id={id}
      border={0}
      positionStrategy={usePortal ? "fixed" : "absolute"}
      style={{ fontSize: "12px", zIndex: 999999999 }}
      className={cn(
        "z-10 rounded-[8px] !bg-gray-dark-600 !px-2 !py-1 !text-gray-dark-1000 shadow-[0px_2px_8px_0px_rgba(0,0,0,0.16)]",
        tooltipClassName
      )}
      place={place}
    >
      {content}
    </Tooltip>
  );

  return (
    <>
      <div
        data-tooltip-id={id}
        data-testid={id}
        data-tooltip-variant="dark"
        className={cn(content ? "cursor-pointer" : "", className)}
      >
        {children}
      </div>
      {usePortal ? (portalRoot ? createPortal(tooltip, portalRoot) : null) : tooltip}
    </>
  );
};
